import {
  Currency,
  CustomParameters,
  PaywallResult,
  PurchasableProduct,
  ScreenType,
} from '@accedo/one-plugin-sdk';
import { FC, useEffect, useState } from 'react';

import { Payment } from '#/components/Payment/Payment';
import { PurchaseSuccess } from '#/components/Status/PurchaseSuccess';
import { Status } from '#/components/Status/Status';
import { handleScreenView } from '#/plugins/analytics/analyticsUtils';
import { AppStatus } from '#/service/constants';
import { falsyToNull, noop } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import { Actions, PurchaseFlowStep } from '../PlanPurchaseFlow.types';
import { StepWrapper } from '../StepWrapper';
import { Discount } from './Discount';
import { usePurchaseProductStep } from './PurchaseProductStep.hook';
import * as Styled from './PurchaseProductStep.styled';
import { SelectedProduct } from './SelectedProduct';
import { SubscribeWithoutPayment } from './SubscribeWithoutPayment';

export interface PurchaseProductStepProps {
  customParameters?: CustomParameters;
  selectedProduct?: PurchasableProduct;
  currentStep: PurchaseFlowStep.PURCHASE;
  actions: Actions;
  onPurchaseComplete: (result: PaywallResult) => void;
  onProductChange: () => void;
}

export const PurchaseProductStep: FC<PurchaseProductStepProps> = ({
  actions: { handleClose },
  selectedProduct,
  currentStep,
  customParameters,
  onProductChange,
  onPurchaseComplete,
}) => {
  const [price, setPrice] = useState<{ amount: string; currency?: Currency }>({
    amount: selectedProduct?.price ?? '0',
    currency: selectedProduct?.currency ?? Currency.UNKNOWN,
  });
  const [isDisabled, setIsDisabled] = useState(false);

  const {
    voucherCode,
    hasOwnUi,
    onVoucherChange,
    onVoucherRemove,
    handlePaymentFailure,
    handlePurchaseComplete,
    handlePurchase,
    isPaymentDetailsRequired,
    payment,
    awaitingEntitlements,
  } = usePurchaseProductStep({
    handleClose,
    customParameters,
    onPurchaseComplete,
    selectedProduct,
  });

  useEffect(() => {
    // iap.setUpYourPayment is considered legacy, but let's keep it as a fallback.
    const screenTitle =
      falsyToNull(tb('iap.selectPlanDialogue.title')) ??
      tb('iap.setUpYourPayment');

    handleScreenView({
      screenType: ScreenType.Purchase,
      screenHeading: screenTitle,
      screenTitle,
    });
  }, []);

  if (payment) {
    if (payment.isExternal) {
      return (
        <Status
          onDismiss={handleClose}
          status={AppStatus.Loading}
          statusMessage={tb('web.iap.external.success.message')}
          statusTitle={tb('iap.loading.title')}
        />
      );
    }

    if (awaitingEntitlements) {
      return (
        <Status
          status={AppStatus.Loading}
          statusMessage={tb('iap.loading.message')}
          statusTitle={tb('iap.loading.title')}
        />
      );
    }

    return (
      <PurchaseSuccess
        message={tb('iap.success.message')}
        onDefaultClose={handleClose}
        title={tb('iap.success.title')}
      />
    );
  }

  if (!selectedProduct) {
    throw new Error('No selected product was found');
  }

  return (
    <StepWrapper step={currentStep}>
      <Styled.Receipt>
        <SelectedProduct
          {...selectedProduct}
          currency={price.currency ?? Currency.UNKNOWN}
          isDisabled={isDisabled}
          onProductChange={onProductChange}
          price={price.amount}
        />
        <Styled.ReceiptLineBreak />
        <Discount
          hasOwnUi={hasOwnUi}
          id={selectedProduct.id}
          isDisabled={isDisabled}
          onPriceChange={setPrice}
          onVoucherChange={onVoucherChange}
          onVoucherRemove={onVoucherRemove}
          price={price}
        />
      </Styled.Receipt>
      <SubscribeWithoutPayment
        isPaymentDetailsRequired={isPaymentDetailsRequired}
        onPaymentComplete={handlePurchaseComplete}
        selectedProduct={selectedProduct}
        voucherCode={voucherCode}
      />
      <Payment
        buyButtonText="web.iap.subscribe.button"
        hasOwnUi={hasOwnUi}
        isDisabled={isDisabled}
        isPaymentDetailsRequired={isPaymentDetailsRequired}
        onFailure={handlePaymentFailure}
        onPurchase={handlePurchase}
        onSuccess={noop}
        paymentMethods={selectedProduct.paymentMethods ?? []}
        setIsDisabled={setIsDisabled}
      />

      <Styled.PaymentFormFooter>
        {tb('web.iap.paymentFooter')}
      </Styled.PaymentFormFooter>
    </StepWrapper>
  );
};
